import moment from "moment";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { addDays } from "../../../../utils/flexible-program";
import {
  NAMING_TOKENS,
  programWeekNumber,
} from "../../../../utils/import-template-selection";
import RichSelect from "./RichSelect";

const pretty = (yyyymmdd) =>
  moment(String(yyyymmdd), "YYYYMMDD").format("MMM D");
const prettyFull = (yyyymmdd) =>
  moment(String(yyyymmdd), "YYYYMMDD").format("MMM D, YYYY");

const NAMING_MODES = ["default", "block", "date", "custom"];

const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

/**
 * Right column: which program, where the import starts, how its weeks are
 * named, and what an overlap or replace-all will do.
 */
const ImportSidePanel = ({
  programName,
  preview,
  today,
  startDate,
  onStartDate,
  naming,
  onNaming,
  namingExample,
  replaceAll,
  conflicts,
  existingCount,
  onUseSuggested,
}) => {
  const { t } = useTranslation("coaching-portal");
  const patternRef = useRef(null);
  const { anchor, suggestedStart, targetWeeks } = preview;

  const weekNumber = (date) => programWeekNumber(anchor, date);
  const why = (week) => {
    if (week.startDate === anchor && replaceAll)
      return t("flexible.importTemplate.startWhy.first");
    if (week.startDate === suggestedStart)
      return t("flexible.importTemplate.startWhy.suggested");
    if (addDays(week.startDate, 6) < today)
      return t("flexible.importTemplate.startWhy.past");
    if (week.sessionCount)
      return t("flexible.importTemplate.startWhy.hasWorkouts", {
        count: week.sessionCount,
      });
    if (week.startDate <= today)
      return t("flexible.importTemplate.startWhy.thisWeek");
    return t("flexible.importTemplate.startWhy.empty");
  };

  const options = targetWeeks.map((week) => {
    const end = addDays(week.startDate, 6);
    const tags = [];
    if (week.startDate === suggestedStart)
      tags.push({
        tone: "info",
        text: t("flexible.importTemplate.tags.suggested"),
      });
    if (week.startDate <= today && today <= end)
      tags.push({
        tone: "success",
        text: t("flexible.importTemplate.tags.thisWeek"),
      });
    else if (end < today)
      tags.push({ tone: "", text: t("flexible.importTemplate.tags.past") });
    if (week.loggedWork)
      tags.push({
        tone: "warning",
        text: t("flexible.importTemplate.loggedWork"),
      });
    else if (week.sessionCount)
      tags.push({
        tone: "",
        text: t("flexible.importTemplate.workoutsCount", {
          count: week.sessionCount,
        }),
      });
    return {
      value: String(week.startDate),
      label: t("flexible.importTemplate.weekShort", {
        week: weekNumber(week.startDate),
      }),
      sub: `${pretty(week.startDate)} – ${pretty(end)}`,
      tags,
      disabled: week.loggedWork,
    };
  });
  const current = targetWeeks.find((week) => week.startDate === startDate);

  // The "Week number" hint shows the names this import will actually save.
  const firstWeekNumber = weekNumber(startDate || anchor);
  const namingOptions = NAMING_MODES.map((mode) => ({
    value: mode,
    label: t(`flexible.importTemplate.naming.${mode}.title`),
    sub: t(`flexible.importTemplate.naming.${mode}.hint`, {
      first: firstWeekNumber,
      second: firstWeekNumber + 1,
    }),
  }));
  const namingCurrent = namingOptions.find((o) => o.value === naming.mode);

  const addToken = (token) => {
    const input = patternRef.current;
    const at = input?.selectionStart ?? naming.pattern.length;
    const end = input?.selectionEnd ?? at;
    const pattern = `${naming.pattern.slice(0, at)}{${token}}${naming.pattern.slice(end)}`;
    onNaming({ ...naming, pattern });
    requestAnimationFrame(() => input?.focus({ preventScroll: true }));
  };

  const overlapWeeks = conflicts.map((week) =>
    weekNumber(Number(week.startDate))
  );
  const overlapWorkouts = conflicts.reduce(
    (total, week) => total + (week.sessions?.length || 0),
    0
  );

  return (
    <>
      <div className="imp-into">
        <span className="imp-avatar">{initials(programName)}</span>
        <div className="imp-into-text">
          <div className="imp-overline">
            {t("flexible.importTemplate.importingInto")}
          </div>
          <div className="imp-into-name">{programName}</div>
        </div>
      </div>

      <div>
        <span className="imp-label">
          {t("flexible.importTemplate.startLabel")}
        </span>
        <RichSelect
          ariaLabel={t("flexible.importTemplate.startLabel")}
          options={options}
          value={String(startDate)}
          onChange={(value) => onStartDate(Number(value))}
          valueLabel={
            current
              ? `${t("flexible.importTemplate.weekShort", {
                  week: weekNumber(current.startDate),
                })} · ${pretty(current.startDate)} – ${prettyFull(addDays(current.startDate, 6))}`
              : ""
          }
          valueSub={current ? why(current) : ""}
        />
      </div>

      <div>
        <span className="imp-label">
          {t("flexible.importTemplate.namingLabel")}
        </span>
        <RichSelect
          ariaLabel={t("flexible.importTemplate.namingLabel")}
          options={namingOptions}
          value={naming.mode}
          onChange={(mode) => onNaming({ ...naming, mode })}
          valueLabel={namingCurrent.label}
          valueSub={namingCurrent.sub}
        />
        {naming.mode === "custom" && (
          <div className="imp-custom">
            <input
              ref={patternRef}
              className="imp-input"
              maxLength={60}
              aria-label={t("flexible.importTemplate.naming.patternLabel")}
              value={naming.pattern}
              onChange={(event) =>
                onNaming({ ...naming, pattern: event.target.value })
              }
            />
            <div className="imp-tokens">
              {NAMING_TOKENS.map((token) => (
                <button
                  key={token}
                  type="button"
                  className="imp-token"
                  onClick={() => addToken(token)}
                >
                  + {t(`flexible.importTemplate.naming.tokens.${token}`)}
                </button>
              ))}
            </div>
            {namingExample && (
              <p className="imp-example">
                {t("flexible.importTemplate.naming.example")}{" "}
                <b>{namingExample}</b>
              </p>
            )}
          </div>
        )}
      </div>

      {replaceAll ? (
        <div className="imp-notice" role="note">
          <img src="/img/new-icons/chip-warning.svg" alt="" />
          <div className="imp-notice-main">
            <div className="imp-notice-title">
              {t("flexible.importTemplate.replaceAllTitle", {
                count: existingCount,
              })}
            </div>
            <p className="imp-notice-text">
              {t("flexible.importTemplate.replaceAllWarning", {
                count: existingCount,
              })}
            </p>
            {suggestedStart !== startDate && (
              <div className="imp-note-actions">
                <button
                  type="button"
                  data-button-type="secondary"
                  className="sm"
                  onClick={onUseSuggested}
                >
                  {t("flexible.importTemplate.useSuggested", {
                    week: weekNumber(suggestedStart),
                  })}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        conflicts.length > 0 && (
          <div className="imp-notice" role="note">
            <img src="/img/new-icons/chip-warning.svg" alt="" />
            <div className="imp-notice-main">
              <div className="imp-notice-title">
                {t("flexible.importTemplate.overlapTitle", {
                  weeks:
                    overlapWeeks.length === 1
                      ? `${overlapWeeks[0]}`
                      : `${overlapWeeks[0]}–${overlapWeeks[overlapWeeks.length - 1]}`,
                  count: overlapWorkouts,
                })}
              </div>
              <p className="imp-notice-text">
                {t("flexible.importTemplate.overlapText")}
              </p>
              {suggestedStart !== startDate && (
                <div className="imp-note-actions">
                  <button
                    type="button"
                    data-button-type="secondary"
                    className="sm"
                    onClick={onUseSuggested}
                  >
                    {t("flexible.importTemplate.useSuggested", {
                      week: weekNumber(suggestedStart),
                    })}
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      )}
    </>
  );
};

export default ImportSidePanel;
